// Pure helpers of the row form (RowForm.jsx). The form itself comes from
// GET /api/projects/{id}/data/tables/{table}/form (backend operate/row_schema.py):
//   {table, primary_key, identity, columns, generated, json_columns, minter, schema, uiSchema}

const isEmpty = v => v === undefined || v === null || v === "";

/** A row as the API returns it -> the form's data (JSON columns as text, empty cells left out). */
export function toFormData(form, row) {
  const out = {};
  const json = new Set(form.json_columns || []);
  (form.columns || []).forEach(name => {
    const v = row ? row[name] : undefined;
    if (isEmpty(v)) return;
    out[name] = json.has(name) && typeof v !== "string" ? JSON.stringify(v, null, 2) : v;
  });
  return out;
}

/** The `values` to send: on create every filled column; on edit only what changed (a cleared
 *  column is sent as null). Generated (build-set) columns are never sent. */
export function toValues(form, formData, initial = null) {
  const skip = new Set(form.generated || []);
  const values = {};
  (form.columns || []).forEach(name => {
    if (skip.has(name)) return;
    const now = formData ? formData[name] : undefined;
    if (initial === null) {
      if (!isEmpty(now)) values[name] = now;
      return;
    }
    const before = initial[name];
    if (isEmpty(now) && isEmpty(before)) return;
    if (isEmpty(now)) values[name] = null;
    else if (now !== before) values[name] = now;
  });
  return values;
}

/** A 422 of the row endpoints ({loc: ["body", "values", column], msg}) -> RJSF extraErrors,
 *  plus the messages that name no column (shown above the form). */
export function rowErrors(detail) {
  const schema = {};
  const general = [];
  (Array.isArray(detail) ? detail : []).forEach(d => {
    const loc = Array.isArray(d.loc) ? d.loc : [];
    const column = loc[0] === "body" && loc[1] === "values" ? loc[2] : undefined;
    if (column === undefined) {
      general.push(d.msg);
      return;
    }
    const k = String(column);
    schema[k] = schema[k] || {};
    schema[k].__errors = [...(schema[k].__errors || []), d.msg];
  });
  return { schema, general };
}

/** The listed messages of a 422: "column: message". */
export function rowErrorList(detail) {
  return (Array.isArray(detail) ? detail : []).map(d => {
    const loc = Array.isArray(d.loc) ? d.loc : [];
    return loc.length > 2 ? `${loc[2]}: ${d.msg}` : d.msg;
  });
}

export const LOOKUP_LIMIT = 50;

/** The lookup request of a foreign-key field (backend operate/browse.py `lookup`): the rows of
 *  fk.table in key order whose fk.column or label contains `q`, or the row whose key is `key`. */
export function lookupPath(projectId, fk, { q = "", key = null, limit = LOOKUP_LIMIT } = {}) {
  const params = new URLSearchParams();
  params.set("column", fk.column);
  if (q) params.set("q", q);
  if (key !== null && key !== undefined) params.set("key", String(key));
  params.set("limit", String(limit));
  return `/projects/${projectId}/data/tables/${encodeURIComponent(fk.table)}/lookup?${params.toString()}`;
}

/** One value of a referenced row as text. */
export function cellText(column, value) {
  if (value === null || value === undefined || value === "") return "—";
  return typeof value === "object" ? JSON.stringify(value) : String(value);
}

/** Text typed into a numeric field -> a number when it is one (JSON Schema validates the type). */
export function typed(schema, text) {
  if (isEmpty(text)) return undefined;
  if (schema && (schema.type === "integer" || schema.type === "number")) {
    const n = Number(text);
    if (String(text).trim() !== "" && Number.isFinite(n)) return n;
  }
  return text;
}
